import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../../../Support/with_presenter';
import {CreateScheduledEventPresenter} from './CreateScheduledEventPresenter';
import {DatePicker} from './Components/DatePicker';
import {Duration} from '../../../../../Domain/Duration';
import {Crew} from '../../../../../Models/Crew';
import {Frequency} from '../../../../../Models/ScheduledEvent';

interface OwnProps {
    crews: Crew[];
    date: Date | null;
    onFinish: () => void;
}

const CreateScheduledEventComponent: FunctionComponent<OwnProps & PresenterProps<CreateScheduledEventPresenter>> =
    observer(({onFinish, crews, presenter}) => {
        return (
            <div className="card">
                <div className="card-title">
                    <h5>Create an event {crews.length === 1 ? 'for "' + crews[0].name + '"' : ''}</h5>
                </div>
                <form
                    method="POST"
                    onSubmit={(e) => {
                        e.preventDefault();
                        presenter.onSubmit();
                        return false;
                    }}
                    className="form-horizontal"
                >
                    <div className="card-body">
                        {crews.length > 1 && (
                            <div className="mb-3">
                                <label htmlFor="crew" className="form-label mb-2">
                                    Crew
                                </label>
                                <select
                                    className="form-select"
                                    name="crew"
                                    id="crew"
                                    value={presenter.crew.id}
                                    onChange={(e) => {
                                        presenter.setCrew(
                                            crews.find((crew) => {
                                                // parseInt since mobx proxied objects are not equal
                                                // @ts-ignore
                                                // tslint:disable-next-line:triple-equals
                                                return parseInt(crew.id, 10) === parseInt(e.target.value, 10);
                                            })!,
                                        );
                                    }}
                                >
                                    <option value="">Select a crew</option>
                                    {crews.map((crew) => (
                                        <option key={crew.id} value={crew.id}>
                                            {crew.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label mb-2">
                                Title
                            </label>
                            <input
                                className="form-control"
                                placeholder="Title"
                                autoFocus
                                name="title"
                                type="text"
                                id="title"
                                value={presenter.title}
                                onChange={(e) => presenter.setTitle(e.target.value)}
                            />
                        </div>
                        <div className="row">
                            <div className="col-12 col-md-6">
                                <div className="mb-3">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="date" className="form-label mb-2">
                                                Date
                                            </label>
                                            <DatePicker
                                                date={presenter.startAt}
                                                onChange={(date) => presenter.setStartAt(date)}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="time" className="form-label mb-2">
                                                Time
                                            </label>
                                            <select
                                                className="form-select"
                                                name="time"
                                                id="time"
                                                value={presenter.time}
                                                onChange={(e) => presenter.setTime(e.target.value)}
                                            >
                                                <option value="">Select a time</option>
                                                {presenter.timeOptions.map((time) => (
                                                    <option key={time} value={time}>
                                                        {time}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="time" className="form-label mb-2">
                                        Duration (min.)
                                    </label>
                                    <input
                                        className="form-control"
                                        placeholder="Duration"
                                        name="duration"
                                        type="text"
                                        id="duration"
                                        value={presenter.duration?.minutes}
                                        onChange={(e) => {
                                            if (e.target.value === '') {
                                                presenter.setDuration(null);
                                            } else {
                                                presenter.setDuration(
                                                    Duration.fromMinutes(parseInt(e.target.value, 10)),
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="frequency" className="form-label mb-2">
                                Repeats (optional)
                            </label>
                            <select
                                className="form-select"
                                name="frequency"
                                id="frequency"
                                value={presenter.frequency === null ? '' : presenter.frequency}
                                onChange={(e) =>
                                    presenter.setFrequency(e.target.value === '' ? null : (e.target.value as Frequency))
                                }
                            >
                                <option value="">Does not repeat</option>
                                {Object.keys(presenter.frequencyOptions).map((frequency, index) => (
                                    <option key={frequency} value={frequency}>
                                        {Object.values(presenter.frequencyOptions)[index]}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label mb-2">
                                Description
                            </label>
                            <input
                                className="form-control"
                                placeholder="Description"
                                name="description"
                                type="text"
                                id="description"
                                value={presenter.description}
                                onChange={(e) => presenter.setDescription(e.target.value)}
                            />
                        </div>
                        <div className="row">
                            <div className="col-12 d-flex justify-content-between">
                                <button onClick={() => onFinish()} className="btn btn-transparent pull-left">
                                    Cancel
                                </button>
                                <button className="btn btn-primary" disabled={!presenter.validates}>
                                    Create
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    });

export const CreateScheduledEvent = withPresenter<CreateScheduledEventPresenter, OwnProps>(
    (props, component) => new CreateScheduledEventPresenter(props.crews, props.date, props.onFinish, component.events),
    CreateScheduledEventComponent,
);
